import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import type { Role } from '../../api/types'
import { formatDate } from '../../lib/format'
import { CopyField } from '../CopyField'

interface Invite {
	email: string
	role: Role
	created_at: string
	expires_at: string
	used_at: string | null
	created_by_name: string | null
}

function inviteState(i: Invite) {
	if (i.used_at) return 'accepted'
	return new Date(i.expires_at) < new Date() ? 'expired' : 'pending'
}

export function InvitesPanel() {
	const client = useQueryClient()
	const invites = useQuery({ queryKey: ['admin-invites'], queryFn: () => api.get<Invite[]>('/api/admin/invites') })
	const [email, setEmail] = useState('')
	const [role, setRole] = useState<Role>('viewer')
	const [link, setLink] = useState<string | null>(null)
	const create = useMutation({
		mutationFn: () => api.post<{ url: string }>('/api/admin/invites', { email, role }),
		onSuccess: (r) => {
			setLink(r.url)
			setEmail('')
			client.invalidateQueries({ queryKey: ['admin-invites'] })
		},
	})
	function submit(e: FormEvent) {
		e.preventDefault()
		create.mutate()
	}
	return (
		<section className="card stack">
			<h2 style={{ margin: 0 }}>Invites</h2>
			<form className="row" onSubmit={submit}>
				<input
					type="email"
					required
					placeholder="name@example.com"
					aria-label="Invite email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					style={{ flex: '1 1 220px' }}
				/>
				<select aria-label="Invite role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
					<option value="viewer">Viewer</option>
					<option value="admin">Admin</option>
				</select>
				<button className="primary" type="submit" disabled={create.isPending}>
					Create invite link
				</button>
			</form>
			{create.isError && <p className="error small">{create.error.message}</p>}
			{link && (
				<div>
					<p className="small" style={{ margin: '0 0 0.3rem' }}>
						Send this link to the invitee (valid 7 days, single use):
					</p>
					<CopyField label="Invite link" value={link} />
				</div>
			)}
			{invites.data && invites.data.length > 0 && (
				<table>
					<tbody>
						{invites.data.map((i) => (
							<tr key={i.email + i.created_at}>
								<td>{i.email}</td>
								<td className="muted small">{i.role}</td>
								<td className="small">{inviteState(i)}</td>
								<td className="muted small">
									{formatDate(i.created_at)}
									{i.created_by_name && ` by ${i.created_by_name}`}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</section>
	)
}
